import { EASY_MODE_STEPS } from "../constants/easyModeSteps";
import { TOUR_DEFAULT_PLACEMENT, TOUR_TARGET_WAIT_MS } from "../constants/tour";
import { TOUR_TARGETS, tourSelector } from "../constants/tourTargets";
import { waitForElement } from "../utils/helper";
import { buildStepRequirement } from "./easyModeTourRequirements";

// Ordered tour content. wizardStep is the step that has to be showing for the
// target to exist. requirement holds Next until the stop has been carried out.
//
// Unlike the edit case tour, no stop is conditional: easy mode renders the same
// three steps for every case, so the list is fixed and cannot resize under a
// running tour. The upload list is the one target that comes and goes, and the
// stop before it is gated on an uploaded document, so it is always mounted by
// the time the tour arrives.
const EASY_MODE_TOUR_STOPS = [
   {
      target: TOUR_TARGETS.EASY_MODE_STEPPER,
      wizardStep: EASY_MODE_STEPS.CASE_INTAKE,
      title: "Three steps, that is it",
      content:
         "Tell us about the case, drop in your documents, and we draft the demand. You can leave and pick it up where you left off.",
      placement: "bottom",
   },
   {
      target: TOUR_TARGETS.EASY_MODE_CASE_FACTS,
      wizardStep: EASY_MODE_STEPS.CASE_INTAKE,
      title: "Basic facts",
      content:
         "Case type, state and incident date decide which analysis runs and which law applies. The party counts tell us how many people to look for in your documents.",
      requirement: buildStepRequirement({
         step: EASY_MODE_STEPS.CASE_INTAKE,
         fallbackHint: "Fill in the required fields to carry on.",
      }),
   },
   {
      target: TOUR_TARGETS.EASY_MODE_CLASSIC_INTAKE,
      wizardStep: EASY_MODE_STEPS.CASE_INTAKE,
      title: "Want to fill in everything yourself?",
      content:
         "Classic Intake is the long form, where you enter every provider and party by hand. You can switch over at any point and nothing is lost.",
   },
   {
      target: TOUR_TARGETS.EASY_MODE_PARTNER_IMPORT,
      wizardStep: EASY_MODE_STEPS.DOCUMENT_UPLOAD,
      title: "Already have this case elsewhere?",
      content:
         "If it lives in your case management system, import it here and we pull the parties, dates and documents across rather than you typing them twice.",
   },
   {
      target: TOUR_TARGETS.EASY_MODE_DROPZONE,
      wizardStep: EASY_MODE_STEPS.DOCUMENT_UPLOAD,
      title: "Drop everything in at once",
      content:
         "Records, bills, police reports, photos. There is no need to sort or label them, and a whole folder is fine. Working out what each file is comes next.",
      requirement: buildStepRequirement({
         step: EASY_MODE_STEPS.DOCUMENT_UPLOAD,
         fallbackHint: "Add at least one document to carry on.",
      }),
   },
   {
      target: TOUR_TARGETS.EASY_MODE_UPLOAD_LIST,
      wizardStep: EASY_MODE_STEPS.DOCUMENT_UPLOAD,
      title: "We sort them out",
      content:
         "Each file is read and classified as it lands. Anything we could not open is flagged here, so it is worth a look before moving on.",
   },
   {
      target: TOUR_TARGETS.EASY_MODE_CONTEXT,
      wizardStep: EASY_MODE_STEPS.DEMAND_GENERATION,
      title: "Anything the documents do not say",
      content:
         "Optional, but this is where context that is not written down anywhere goes. Prior injuries, a disputed account of the incident, anything you want the letter to handle carefully.",
   },
   {
      target: TOUR_TARGETS.EASY_MODE_GENERATE,
      wizardStep: EASY_MODE_STEPS.DEMAND_GENERATION,
      title: "Then we write it",
      content:
         "We draft the demand from everything you have given us and tell you what is still missing. It takes a few minutes and uses a credit.",
      placement: "top",
   },
];

// The stops the tour visits, in order, so a running tour's index can be mapped
// back to the stop it is showing
export const getEasyModeTourStops = () => EASY_MODE_TOUR_STOPS;

export const buildEasyModeTourSteps = ({ goToWizardStep }) =>
   EASY_MODE_TOUR_STOPS.map(({ target, wizardStep, title, content, placement }) => {
      const selector = tourSelector(target);

      return {
         target: selector,
         title,
         content,
         placement: placement ?? TOUR_DEFAULT_PLACEMENT,
         // Joyride calls .then on this unconditionally, so it always resolves. A
         // step with a before hook never reaches the library's own target
         // polling, and an anchor that is not mounted the moment this settles is
         // skipped outright, so the wait belongs here
         before: async () => {
            await goToWizardStep(wizardStep);
            await waitForElement({ selector, timeoutMs: TOUR_TARGET_WAIT_MS });
         },
      };
   });

export default EASY_MODE_TOUR_STOPS;
